import type { ElementRef } from '@angular/core';
import { ChangeDetectorRef, Component, ViewChild, output } from '@angular/core';
import { UntypedFormControl, Validators, ReactiveFormsModule } from '@angular/forms';

import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import { CreditCardIconPipe } from '@core/billing-ui';
import { filterBoolean } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  IconComponent,
  InputComponent,
  LabelComponent,
  FormFieldComponent,
} from '@core/user-interface-legacy';

import type { Element } from '../../../types/stripe.interfaces';
import { RtStripePoweredByComponent } from '../stripe-powered-by/stripe-powered-by.component';
import { StripeService } from '../stripe.service';

interface StripeElements {
  cardNumber: Element;
  cardExpiry: Element;
  cardCvc: Element;
}

@Component({
  selector: 'rt-stripe-card',
  templateUrl: './stripe-card.component.html',
  styleUrls: ['./stripe-card.component.less'],
  imports: [
    FormFieldComponent,
    LabelComponent,
    InputComponent,
    ReactiveFormsModule,
    IconComponent,
    RtStripePoweredByComponent,
    CreditCardIconPipe,
    TranslatePipe,
  ],
})
export class StripeCardComponent {
  public changed = output<{
    complete: boolean;
    card: Element;
    name: string;
  }>();

  public tokenChange = output<string>();

  public nameControl: UntypedFormControl = new UntypedFormControl('', [Validators.required]);

  public cardNumberComplete$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public cardExpiryComplete$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public cardCvcComplete$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public brand: string;
  public cvcDigits: number = 3;

  @ViewChild('cardNumber', { static: true })
  private cardNumber: ElementRef;

  @ViewChild('cardExpiry', { static: true })
  private cardExpiry: ElementRef;

  @ViewChild('cardCvc', { static: true })
  private cardCvc: ElementRef;

  private ngOnDestroy$ = new Subject<void>();

  private elements: StripeElements;

  constructor(
    private stripeService: StripeService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.stripeService.elements$
      .asObservable()
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(elements => {
        this.unmountElements();
        this.elements = elements;
        this.mountElements();
        this.attachListeners();
      });
  }

  public ngOnDestroy(): void {
    this.unmountElements();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private mountElements(): void {
    Object.entries(this.elements).forEach(([key, element]: [keyof StripeElements, Element]) => {
      element.mount(this[key].nativeElement);
    });
  }

  private attachListeners(): void {
    this.elements.cardNumber.on('change', event => {
      if (event.brand) {
        this.brand = event.brand;
        this.cvcDigits = event.brand === 'amex' ? 4 : 3;
      }
      this.cardNumberComplete$.next(event.complete);
    });

    this.elements.cardExpiry.on('change', event => {
      this.cardExpiryComplete$.next(event.complete);
    });

    this.elements.cardCvc.on('change', event => {
      this.cardCvcComplete$.next(event.complete);
    });

    combineLatest([
      this.cardNumberComplete$,
      this.cardExpiryComplete$,
      this.cardCvcComplete$,
      this.nameControl.valueChanges.pipe(startWith(this.nameControl.value)),
    ])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(complete => {
        this.changed.emit({
          complete: complete.every(c => c) && this.nameControl.valid,
          card: this.elements.cardNumber,
          name: this.nameControl.value,
        });
        this.cdRef.detectChanges();
      });
  }

  private unmountElements(): void {
    if (this.elements) {
      Object.entries(this.elements).map(([_, element]) => {
        element.unmount();
        element.clear();
      });
    }
  }
}
